
import { gsap, Power2 } from 'gsap';
import { GameConfig, SanGameMode } from "../../../config/base/GameConfig";
import { StateHandler } from "../../../../core/utils/StateHandler";
import { Presentation } from "../../../../core/gameflow/controller/Presentation";
import { IWinRatioConfig } from "../../../config/base/IWinConfig";
import { GameData } from "../../../server/data/GameData";
import { SoundManager } from "../../../config/sound/SoundManager";
import { SoundConfig } from "../../../config/sound/SoundConfig";
import { SanQuentin2 } from "../../../SanQuentin2";
import { RevealerManager } from "../../../modules/revealer/RevealerManager";
import { Revealer } from "../../../modules/revealer/Revealer";
import { SanEngineManager } from "../../../engine/SanEngineManager";
import { BigWinPosterState } from "../../../modules/poster/BigWinPoster";
import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { GameSetting } from '../../../../core/setting/GameSetting';
import { Speedster } from '../../../../core/debug/Speedster';
import { ArrayHelper } from '../../../../core/utils/ArrayHelper';
import { ParticlesManager } from '../../../modules/particles/ParticlesManager';
import { MathHelper } from '../../../../core/utils/MathHelper';

enum BigWinStates {
    START,
    SHOW,
    STOP,
    DONE,
}

export class BigWinPresentation extends Presentation {

    private state = new StateHandler(undefined, new Map<BigWinStates, Function>([
        [BigWinStates.START, (skip: Function) => this.preWin(skip)],
        [BigWinStates.SHOW, (skip: Function) => this.playBigWinLevelAnimation(skip)],
        [BigWinStates.STOP, (skip: Function) => this.postWin(skip)],
        [BigWinStates.DONE, (skip: Function) => {this.setPlayable(false) }],
    ]));


    private _winRatios: IWinRatioConfig[];

    private _winLevel: number = 0;
    private _currLevel: number = 0;

    private _bounceDuration: number = 0.35;

    public initialization() {
        this._winRatios = GameConfig.WIN_RATIOS.bigWin;
    }

    public setGameData(data: GameData): void {
        super.setGameData(data);
        
        this._winLevel = 0;
        this._currLevel = 0;

        this.state.set(BigWinStates.START);

        this._winLevel = this._winRatios.findIndex((indx) => indx.ratio == data.Win.winRatioConfig?.ratio);

        if(this.betValueRatio(this._winLevel) == this.nextValueRatio(this._winLevel)){
            if (this._winLevel == 0)
                this.setPlayable(false);
            else{
                this._winLevel--;
            }
        }
        // if (this._winLevel == 0 && this.betValueRatio(this._winLevel) == this.nextValueRatio(this._winLevel)) {
        //     this.setPlayable(false);
        // }
    }

    public checkForActivation(data: GameData): boolean {
        if(GameSetting.isDevMode && Speedster.ignoreWins) return false;
        return data.Win.isBigWin && !(data.Win.isWinBelowStake() && data.isNormalMode);
    }

    public createPresentation(skip: Function): void {
        this.state.triggerState(skip);
    }

    private preWin(skip:Function): void {
        skip(false);
        this.state.set(BigWinStates.SHOW);
        const SoundDurr = 0.980; // Sound MS

        SoundManager.playSoundEffect(SoundConfig.BIG_WIN_INIT);
        SoundManager.playSoundEffect(ArrayHelper.fetchLastValue(SoundConfig.HIGHLIGHTWIN_LVL))
        SoundManager.FadeAmbience(0,SoundDurr);

        const glowDurr: number = 0.5;

        let timerStart:number = 0;
        let reelOffset:number = 0.1;

        for (let reel = 0; reel < this._gameData.Win.allBetWayWinData.reelWinPositions.length; reel++) {
            let posRow: boolean[] = this._gameData.Win.allBetWayWinData.reelWinPositions[reel];
            for (let row = 0; row < posRow.length; row++) {
                if (row == 0 || row == posRow.length - 1) {
                    const bar: Revealer = row == 0 ? RevealerManager.getTopBar(reel) : RevealerManager.getBotBar(reel);
                    if(bar.isActive){
                        if (posRow[row]){
                            this.timeline.add(() => { bar.playBigWin(glowDurr, 3) }, timerStart)
                        }else{
                            this.timeline.add(() => { bar.markNonWin(true) }, timerStart)
                        }
                    }
                }
                else{
                    const sym = SanEngineManager.getReel(reel).getSymbol(row - 1)!;
                    if (posRow[row]){
                        this.timeline.add(() => { sym.playBigWin(glowDurr, 3) }, timerStart);
                    }else{
                        this.timeline.add(() => { sym.markNonWin(true) }, timerStart)
                    }
                }
            }

            timerStart += reelOffset;
        }

        SanQuentin2.counter.playBigWin(0, this.betValueRatio(0), SoundDurr);
        this.timeline.add(SanQuentin2.counter.moveDown(SoundDurr/2),SoundDurr/2);

        // SanQuentin2.BigWinPoster.kaboom(true);
        // this.timeline.fromTo(SanQuentin2.counter, { alpha: 0 }, { alpha : 1 , duration:durr},0);
    }

    public playBigWinLevelAnimation(skip:Function): void {
        skip(true);

        const presLvl: number = this._currLevel >= this._winLevel ? this._winLevel : this._currLevel;
        let countUpDurr: number = this._winRatios[presLvl].duration - 0.03; // Hearing a glitch so I reduced the duration by to prevent this 20ms.

        SoundManager.playBigWin(presLvl + 1);
        SanQuentin2.counter.bounceHit(presLvl + 5, this._bounceDuration);
        SanQuentin2.BorderFire.PulseLevel(presLvl);
        this.playBurst(presLvl);
        this.playRain(presLvl);
        SanQuentin2.BigWinPoster.splash();
        if(presLvl >= 4) SanQuentin2.BigWinPoster.kaboom(presLvl-4);

        SanQuentin2.BigWinPoster.play(BigWinPosterState.BIG_WIN, { animation: BigWinPosterState.bigWinSpinePath(presLvl), loop: true }, (!presLvl ? 0.2 : 0));
        SanQuentin2.reelEngineManager.bounceScreen(1.075,0.6);
        this.timeline.add(this.playBigWinCountUp(presLvl, countUpDurr - this._bounceDuration), this._bounceDuration);
            
        if (this._currLevel == this._winLevel)
            this.state.set(BigWinStates.STOP);
        else
            this._currLevel++;
    }

    private postWin(skip:Function): void {
        skip(false);

        if(!this._gameData.Win.brokeTheBank)
            SoundManager.FadeAmbience(1,2000);

        SoundManager.stopSoundEffect(SoundConfig.BIG_WIN_LVL + (Math.min(this._winLevel, 5) + 1));
        SoundManager.playSoundEffect(SoundConfig.BIG_WIN_ENDS);
        if(this._gameData.Win.brokeTheBank){
            SoundManager.playSoundEffect(SoundConfig.BIG_WIN_ENDS_CAP);
        }

        SanQuentin2.BorderFire.killFire();

         SanQuentin2.particleManager.allBursts.forEach(b => b.enableEmit(false));

         SanQuentin2.particleManagerBehind.moneyRain(0).enableEmit(false);
         SanQuentin2.particleManager.moneyRain(0).enableEmit(false);

        let animDuration: number = this._winLevel > 5 ? 2.5 : 1.25;
        SanQuentin2.counter.bounceHit(3, this._bounceDuration);
        SanQuentin2.BigWinPoster.splash();
         SanQuentin2.particleManager.paperRain.enableEmit(false);
        // SanQuentin2.BigWinPoster.kaboom(false);

        this.timeline.add(() => { 
            SanQuentin2.counter.moveToReset(1);
            SanQuentin2.counter.showWays(0.2,this._gameData.Win.allBetWayWinData.totalWinCombination);
        }, this._bounceDuration);

        const delPos: number = this._bounceDuration+1;

        this.timeline.add(() => {
            SanQuentin2.counter.stop(this._gameData!.Win.singleWin, animDuration, 0,true);    
        }, delPos)

        for (let reel = 0; reel < this._gameData.Win.allBetWayWinData.reelWinPositions.length; reel++) {
            let posRow: boolean[] = this._gameData.Win.allBetWayWinData.reelWinPositions[reel];
            const startPos: number = delPos;
            for (let row = 0; row < posRow.length; row++) {
                const lvl: number = this._gameData.Win.allBetWayWinData.biggestWinLvl;

                if (row == 0 || row == posRow.length - 1) {
                    const bar: Revealer = row == 0 ? RevealerManager.getTopBar(reel) : RevealerManager.getBotBar(reel);
                    if(bar.isActive){
                        if (posRow[row]){
                            this.timeline.add(() => { bar.stopBigWin(animDuration*1.5, lvl) }, startPos)
                        }else{
                            this.timeline.add(() => { bar.markNonWin(false, 0, animDuration*1.5) }, startPos)
                        }
                    }
                }
                else{
                    if (posRow[row]){
                        this.timeline.add(() => { SanEngineManager.getReel(reel).getSymbol(row - 1)!.stopBigWin(animDuration*1.5) }, startPos);
                    }else{
                        this.timeline.add(() => { SanEngineManager.getReel(reel).getSymbol(row - 1)!.markNonWin(false,0,animDuration*1.5) }, startPos);

                    }
                }
            }
        }
        this.timeline.to(this, { duration : delPos-0.25 });
        // this.timeline.to(this, {duration:animDuration});
     
        SlotGame.keypadPlugin.setWin(this._gameData.Win.totalWin,
            this._gameData.Win.shouldShowTotalWin,
             this._gameData.Win.isWinBelowStake())
        
        SoundManager.stopBigWin();

        SanQuentin2.BigWinPoster.stop(this.timeline.totalDuration());
        this.state.set(BigWinStates.DONE);
    }

    public playBurst(lvl:number){
        SanQuentin2.particleManager.allBursts[MathHelper.clamp(lvl-1,0, SanQuentin2.particleManager.allBursts.length-1)].enableEmit(false);
         SanQuentin2.particleManager.allBursts[MathHelper.clamp(lvl,0, SanQuentin2.particleManager.allBursts.length-1)].enableEmit(true);
        if(lvl > 4)  SanQuentin2.particleManager.paperRain.enableEmit(true);
    }

    public playRain(lvl:number){
        // return;
        SanQuentin2.particleManagerBehind.moneyRain(0).enableEmit(true);
        SanQuentin2.particleManager.moneyRain(0).enableEmit(true);
    }

    protected playBigWinCountUp(level: number, duration: number): gsap.core.Timeline{
        const fromValue: number = this.betValueRatio(level);
        const toValue: number = this.nextValueRatio(level);
        return SanQuentin2.counter.play(fromValue, toValue, duration);
    }

    public done(): void {
    }

    private betValueRatio(level: number): number {
        return this._gameData!.Win.playedBetValue * this._winRatios[level].ratio;
    }

    private nextValueRatio(level: number): number {
        return level + 1 > this._winLevel ? this._gameData.Win.singleWin : this.betValueRatio(level + 1);
    }
}